import { clsx } from 'clsx';
import {
  Children,
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  ReactNode,
  RefAttributes,
} from 'react';

import { unstable_composeClasses as composeClasses } from '@mui/material';
import MuiPaper, { PaperProps as MuiPaperProps } from '@mui/material/Paper';

import { styled, useTheme } from '@mui/material/styles';
import { padding, PaddingProps, shouldForwardProp as sysShouldForwardProp } from '@mui/system';

import Box, { BoxProps } from '../Box';
import { BackgroundColorList, ColorList } from '../theme/colorList';
import colors from '../theme/colors';
import Footer from './Footer';
import { getPaperUtilityClass } from './paperClasses';

export * from '@mui/material/Paper';

interface StyledPaperOwnerState extends Pick<MuiPaperProps, 'onClick'> {
  /**
   * Background color of the pane. The `'default'` option means the default page background.
   */
  color?: BackgroundColorList | 'transparent';

  /**
   * Left accent color (defaults to none).
   */
  accentColor?: ColorList | 'gray';

  /**
   * If truthy, the `borderColor` prop value will be overridden with 'primary', signifying the Paper is selected.
   */
  selected?: boolean;

  /**
   * What color to show the 1px solid border in. If undefined, there will be no border.
   *
   * This prop has lower precedence than `selected`!
   * @default 'transparent'
   */
  borderColor?: ColorList | 'gray' | 'transparent';

  /**
   * Displays Chip over the top left corner and extends styles for absolute positioned Chip element.
   */
  chip?: ReactNode;

  /**
   * Should be the border wider (set to 2px)
   *
   */
  wideBorder?: boolean;

  /**
   * Should have 4px outline in page background color
   *
   */
  outline?: boolean;
}

export interface PaperProps extends StyledPaperOwnerState, Omit<MuiPaperProps, 'color' | 'variant'>, PaddingProps {
  /**
   * Props to be passed to the content container (= a box containing everything except potential footers of the Paper).
   */
  contentProps?: BoxProps;

  /**
   * @default 0
   */
  elevation?: MuiPaperProps['elevation'];

  /**
   * Preferred way to set content padding. Defaults to even padding (same on both axes) based on viewport size (32px on XL). If
   * 'grilled' is passed, a different (again breakpoint-based) preset is used (16px 32px on XL).
   */
  p?: PaddingProps['p'] | 'grilled';

  /**
   * Displays Chip over top left corner on paper. Accepts <Chip> component with special boolean prop "hangout".
   */
  chip?: ReactNode;
}

const useUtilityClasses = ({ classes, selected }: Pick<PaperProps, 'classes' | 'selected'>) => {
  const slots = {
    root: ['root', selected && 'selectedTrue'],
    content: ['content'],
    footer: ['footer'],
  };

  const composedClasses = composeClasses(slots, getPaperUtilityClass, classes);

  return {
    ...classes,
    ...composedClasses,
  };
};

const StyledMuiPaper = styled(MuiPaper, {
  name: 'O2Paper',
  slot: 'Root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop),
})<{ ownerState: StyledPaperOwnerState }>(
  ({ theme, ownerState: { accentColor, borderColor, color, onClick, selected, chip, wideBorder, outline } }) => ({
    border: `1px solid ${
      // selected always takes precedence
      selected
        ? theme.palette.primary.main
        : borderColor === 'gray'
        ? colors.transparentGray['200']
        : // unset or 'transparent' -> transparent
        !borderColor || borderColor === 'transparent'
        ? 'transparent'
        : theme.palette[borderColor].main
    }`,
    ...(onClick && {
      cursor: 'pointer',
    }),
    ...(accentColor && {
      borderLeftWidth: 8,
      borderLeftStyle: 'solid',
      borderLeftColor: accentColor === 'gray' ? colors.transparentGray['400'] : theme.palette[accentColor].main,
    }),
    ...(color && {
      backgroundColor: color === 'transparent' ? 'transparent' : theme.palette.background[color],
    }),
    ...(chip && {
      position: 'relative',
      marginTop: 8,
    }),
    '& > .O2Paper-footer': {
      border: 'inherit',
      borderTop: 'none',
      borderBottomLeftRadius: 'inherit',
      borderBottomRightRadius: 'inherit',
      ...(accentColor && {
        marginLeft: 0,
        borderLeft: 'none',
      }),
    },
    '& > .MuiChip-root:first-of-type': {
      ...(chip &&
        accentColor && {
          left: 16,
        }),
    },
    ...(wideBorder && {
      borderTopWidth: '2px',
      borderRightWidth: '2px',
      borderBottomWidth: '2px',
      borderLeftWidth: '2px',
      ...(accentColor && {
        borderLeftWidth: '8px',
      }),
    }),
    ...(outline && {
      outline: `4px solid ${colors.basic['200']}`,
    }),
  })
);

const PaperContentWithSpacing = styled(Box, {
  name: 'O2Paper',
  slot: 'Content',
})(() => ({}));

type Paper = ForwardRefExoticComponent<PropsWithoutRef<PaperProps> & RefAttributes<HTMLDivElement>> & {
  Footer: typeof Footer;
};

// eslint-disable-next-line react/jsx-curly-brace-presence
const footerElementType = (<Footer>{''}</Footer>).type;

const isFooter = (child: ReactNode): boolean => {
  return typeof child === 'object' && child !== null && 'type' in child && child.type === footerElementType;
};

export const defaultPaperPaddings = {
  xl: 3,
  lg: 2.5,
  md: 2.5,
  sm: 2.5,
  xs: 2,
};

const Paper = forwardRef<HTMLDivElement, PaperProps>(
  (
    {
      accentColor,
      borderColor = 'transparent',
      color = 'white',
      classes,
      children,
      elevation = 0,
      p = defaultPaperPaddings,
      selected = false,
      contentProps,
      chip,
      wideBorder,
      outline,
      ...other
    },
    ref
  ) => {
    const theme = useTheme();
    const { onClick } = other;
    let computedP = p;

    if (computedP === 'grilled') {
      computedP = {
        xl: theme.spacing(2, 3),
        sm: theme.spacing(2, 2.5),
        xs: theme.spacing(2),
      };
    }

    /**
     * If Chip is over top left corner, make top padding bigger on mobile.
     */
    if (chip) {
      computedP = {
        ...computedP,
        xs: theme.spacing(3, 2, 2),
      };
    }

    const paddingProps: PaddingProps = Object.entries({ p: computedP, ...other })
      .filter(([propName]) => padding.filterProps.includes(propName))
      .reduce((accu, [k, v]) => ({ ...accu, [k]: v }), {});

    const utilityClasses = useUtilityClasses({ classes, selected });

    const content = Children.map(children, (child) => (isFooter(child) ? null : child));

    const footers = Children.map(children, (child) =>
      child && isFooter(child) ? (
        <Footer {...paddingProps} key={(child as ReactElement).key} {...(child as ReactElement).props} />
      ) : null
    );

    return (
      <StyledMuiPaper
        className={clsx(utilityClasses.root)}
        elevation={elevation}
        ref={ref}
        ownerState={{
          accentColor,
          borderColor,
          color,
          onClick,
          selected,
          chip,
          wideBorder,
          outline,
        }}
        {...other}
      >
        {chip}
        <PaperContentWithSpacing className={clsx(utilityClasses.content)} {...paddingProps} {...contentProps}>
          {content}
        </PaperContentWithSpacing>
        {footers}
      </StyledMuiPaper>
    );
  }
) as Paper;

Paper.displayName = 'Paper';
Paper.Footer = Footer;

export default Paper;
